import {
  GALLERY_CHANGE_EVENT,
  GALLERY_PAYWALL_DIALOG_OPEN_EVENT,
} from "./events.js";
import { InteractionObserver } from "./utils";

export type ChangeEvent = CustomEvent<{
  adTagIds?: string[];
  count: number;
  galleryHeadline?: string;
  itemIndex?: string;
  itemNumber?: string;
  itemType?: string;
  itemUrl?: string;
  totalItems?: string;
  totalItemsWithAds?: string;
}>;

const template = document.createElement("template");

template.innerHTML = `
  <style>
    :host {
      display: block;
      position: relative;
    }

    :host([hidden]) {
      display: none;
    }

    :host([text-position="bottom"]) #base {
      background-color: var(--ws-gallery-headline-background-color);
    }

    :host([text-position="cover"]) #navigation {
      gap: 12px;
      inset-block-end: var(--ws-gallery-space-block);
    }

    :host([text-position="cover"]) #item-counter {
      backdrop-filter: blur(6px);
      background-color: var(--ws-gallery-item-counter-background-color);
      inset-block-start: var(--ws-gallery-space-block);
      inset-inline-end: var(--ws-gallery-space-inline);
    }

    #scroll-container {
      display: flex;
      overflow-x: auto;
      scroll-snap-type: x mandatory;
      scrollbar-width: none;
    }

    #scroll-container::-webkit-scrollbar {
      display: none;
    }

    #items::slotted(*) {
      flex: none;
      scroll-snap-align: start;
      inline-size: 100%;
    }

    #item-counter {
      border-radius: 4px;
      color: var(--ws-gallery-item-counter-color);
      display: var(--ws-gallery-item-counter-display, block);
      padding-block: var(--ws-gallery-item-counter-padding-block);
      padding-inline: var(--ws-gallery-item-counter-padding-inline);
      position: absolute;
    }

    #item-counter[hidden] {
      display: none;
    }

    #current-item {
      color: var(--ws-gallery-current-item-color);
      font-weight: var(--ws-gallery-current-item-font-weight);
    }

    #base {
      box-sizing: border-box;
      min-block-size: var(--ws-gallery-base-min-block-size);
      padding-block: var(--ws-gallery-base-padding-block);
      padding-inline: var(--ws-gallery-base-padding-inline);
    }

    #navigation {
      display: flex;
      inset-inline-end: var(--ws-gallery-space-inline);
      position: absolute;
    }

    @media (prefers-reduced-motion: no-preference) {
      :host {
        scroll-behavior: smooth;
      }
    }
  </style>
  <div id="scroll-container"><slot id="items"></slot></div>
  <div id="item-counter"><span id="current-item"></span>/<span id="total-items"></span></div>
  <div id="base">
    <slot name="headline"></slot>
  </div>
  <nav id="navigation" part="navigation">
    <slot name="previous"></slot>
    <slot name="next"></slot>
  </nav>
`;

export class Gallery extends HTMLElement {
  #boundUpdateNavigationPosition: () => void;
  #currentItem: HTMLElement;
  #currentItemNodeIndex = 0;
  #interactionObserver?: InteractionObserver;
  #intersectionCounter = 0;
  #itemCounter: HTMLElement;
  #items: HTMLElement[];
  #itemsObserver: IntersectionObserver;
  #mutationObserver: MutationObserver;
  #navigation: HTMLElement;
  #nextButton: HTMLAnchorElement;
  #prevButton: HTMLAnchorElement;
  #resizeObserver: ResizeObserver;
  #scrollContainer: HTMLElement;
  #slots: Record<string, HTMLSlotElement> = {};
  #textPosition: string;
  #totalItems: HTMLElement;
  #touchStartX = 0;
  #touchStartY = 0;

  get loading() {
    return this.getAttribute("loading") ?? "eager";
  }

  get totalItems() {
    return this.getAttribute("total-items") ?? "0";
  }

  get dataHeadline() {
    return this.getAttribute("data-headline") ?? "";
  }

  get dataTotalItems() {
    return this.getAttribute("data-total-items") ?? "";
  }

  get textPosition() {
    return this.getAttribute("text-position") ?? "bottom";
  }

  isRestricted() {
    return this.hasAttribute("data-is-restricted");
  }

  constructor() {
    super();

    // Setup Shadow DDOM
    this.attachShadow({ mode: "open" });
    this.shadowRoot!.appendChild(template.content.cloneNode(true));

    // Setup HTML elements
    this.#currentItem = this.shadowRoot!.getElementById("current-item")!;
    this.#itemCounter = this.shadowRoot!.getElementById("item-counter")!;
    this.#slots.items = this.shadowRoot!.getElementById(
      "items",
    )! as HTMLSlotElement;
    this.#items = this.#slots.items.assignedElements()! as HTMLElement[];
    this.#slots.next = this.shadowRoot!.querySelector(
      `slot[name="next"]`,
    )! as HTMLSlotElement;
    this.#nextButton =
      this.#slots.next.assignedElements()[0]! as HTMLAnchorElement;
    this.#navigation = this.shadowRoot!.getElementById(
      "navigation",
    ) as HTMLElement;
    this.#slots.previous = this.shadowRoot!.querySelector(
      `slot[name="previous"]`,
    )! as HTMLSlotElement;
    this.#prevButton =
      this.#slots.previous.assignedElements()[0]! as HTMLAnchorElement;
    this.#scrollContainer =
      this.shadowRoot!.getElementById("scroll-container")!;
    this.#totalItems = this.shadowRoot!.getElementById("total-items")!;
    this.#textPosition = this.textPosition;
    this.#boundUpdateNavigationPosition = () =>
      this.#updateNavigationPosition(false);

    // Setup IntersectionObserver
    this.#itemsObserver = new IntersectionObserver(this.#handleIntersection, {
      root: this.#scrollContainer,
      rootMargin: "0px",
      threshold: 0.6,
    });

    // Setup InteractionObserver
    if (this.loading === "interaction") {
      this.#interactionObserver = new InteractionObserver(
        this.#handleInteraction,
      );
    }

    // Setup MutationObserver
    this.#mutationObserver = new MutationObserver(this.#handleMutation);

    //Setup ResizeObserver
    this.#resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        // Do not use scrollBy, it has issues with zoom on Safari 18.0.1
        this.#scrollContainer.scroll({
          behavior: "instant", // Prevent scrolling issues in combination with css scroll-snap-type
          left: entry.contentRect.width * this.#currentItemNodeIndex,
        });
      }
    });
  }

  connectedCallback() {
    // Setup item counter
    this.#totalItems.textContent = this.totalItems;

    // Setup IntersectionObserver for each item
    // Button visibility and item counter are updated on the initial intersection callback.
    for (const item of this.#items) {
      this.#itemsObserver.observe(item);
    }

    this.#resizeObserver.observe(this.#scrollContainer);

    if (this.#textPosition === "bottom") {
      this.#updateNavigationPosition();
      window.addEventListener("resize", this.#boundUpdateNavigationPosition);
    }

    // Setup MutationObserver to add dynamically loaded items to the IntersectionObserver
    this.#mutationObserver.observe(this, { childList: true });

    // Setup event listeners
    if (this.#nextButton && !this.isRestricted()) {
      this.#nextButton.addEventListener("click", this.#scrollNext);
    }
    if (this.#prevButton && !this.isRestricted()) {
      this.#prevButton.addEventListener("click", this.#scrollPrevious);
    }

    if (this.isRestricted()) {
      this.#scrollContainer.addEventListener("wheel", this.#handleWheel);
      this.#scrollContainer.addEventListener(
        "touchstart",
        this.#handleTouchStart,
      );
      this.#scrollContainer.addEventListener(
        "touchmove",
        this.#handleTouchMove,
      );
    }

    this.loading === "interaction"
      ? this.#interactionObserver?.observe(this)
      : this.#handleInteraction();
  }

  getViewportElement() {
    return this.#scrollContainer;
  }

  #handleInteraction = () => {
    this.#interactionObserver?.disconnect();

    // Activate all items
    this.#items.forEach((element) => {
      element.removeAttribute("disabled");
    });
  };

  #handleWheel = (event: WheelEvent) => {
    if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) {
      event.preventDefault();
      this.#dispatchOpenPaywallDialogEvent();
    }
  };

  #handleTouchStart = (event: TouchEvent) => {
    const touch = event.touches[0];
    this.#touchStartX = touch!.clientX;
    this.#touchStartY = touch!.clientY;
  };

  #handleTouchMove = (event: TouchEvent) => {
    const touch = event.touches[0];
    const touchEndX = touch!.clientX;
    const touchEndY = touch!.clientY;
    const deltaX = touchEndX - this.#touchStartX;
    const deltaY = touchEndY - this.#touchStartY;

    if (Math.abs(deltaX) > Math.abs(deltaY)) {
      event.preventDefault();
      this.#dispatchOpenPaywallDialogEvent();
    }
  };

  // Triggers also on intersection of first gallery item (not only when navigating through the gallery)
  #handleIntersection = (entries: IntersectionObserverEntry[]) => {
    const galleryHeadline = this.dataHeadline;
    const totalItemsWithAds = this.dataTotalItems;

    for (const entry of entries) {
      if (entry.isIntersecting && entry.target instanceof HTMLElement) {
        //Set #currentItemNodeIndex to pin position in scroll container
        this.#currentItemNodeIndex = Array.prototype.indexOf.call(
          this.#slots.items!.assignedElements(),
          entry.target,
        );

        const itemIndex = entry.target.dataset.itemIndex;
        const itemNumber = entry.target.dataset.itemNumber;
        let itemType = entry.target.dataset.itemType;
        // a requirement from the data team
        if (itemType === "image-element") itemType = "image";

        this.#updateItemCounter(itemNumber);

        // Re-enable component
        const itemUrl = entry.target.dataset.itemUrl;
        const totalItems = this.totalItems;

        this.#updateBrowserUrl(itemUrl);
        this.#updateButtonsVisibility();

        if (this.#intersectionCounter > 0) {
          this.#dispatchChangeEvent(
            this.#intersectionCounter,
            galleryHeadline,
            itemIndex,
            itemNumber,
            itemType,
            itemUrl,
            totalItems,
            totalItemsWithAds,
          );
        }
        this.#intersectionCounter++;
      }
    }
  };

  #handleMutation = (mutationList: MutationRecord[]) => {
    for (const mutation of mutationList) {
      if (mutation.type === "childList") {
        if (mutation.addedNodes.length > 0) {
          mutation.addedNodes.forEach((node) => {
            if (node instanceof HTMLElement) {
              this.#itemsObserver.observe(node);
            }
          });
        }
        if (mutation.removedNodes.length > 0) {
          mutation.removedNodes.forEach((node) => {
            if (node instanceof HTMLElement) {
              this.#itemsObserver.unobserve(node);
            }
          });
        }
      }
    }
  };

  #dispatchChangeEvent = (
    count: number,
    galleryHeadline?: string,
    itemIndex?: string,
    itemNumber?: string,
    itemType?: string,
    itemUrl?: string,
    totalItems?: string,
    totalItemsWithAds?: string,
  ) => {
    const event: ChangeEvent = new CustomEvent(GALLERY_CHANGE_EVENT, {
      detail: {
        count,
        galleryHeadline,
        itemIndex,
        itemNumber,
        itemType,
        itemUrl,
        totalItems,
        totalItemsWithAds,
        adTagIds: [
          "skyscraper_1",
          "skyscraper_2",
          "skyscraper_3",
          "skyscraper_4",
          "superbanner_1",
          "wallpaper_1",
        ],
      },
    });

    this.dispatchEvent(event);
  };

  #dispatchOpenPaywallDialogEvent = () => {
    document.dispatchEvent(new CustomEvent(GALLERY_PAYWALL_DIALOG_OPEN_EVENT));
  };

  #scrollNext = (event: MouseEvent) => {
    event.preventDefault();
    // Do not use scrollBy, it has issues with zoom on Safari 18.0.1
    this.#scrollContainer.scroll({
      behavior: "instant", // Prevent scrolling issues in combination with css scroll-snap-type
      left:
        this.#scrollContainer.scrollLeft + this.#scrollContainer.clientWidth,
    });
  };

  #scrollPrevious = (event: MouseEvent) => {
    event.preventDefault();
    // Do not use scrollBy, it has issues with zoom on Safari 18.0.1
    this.#scrollContainer.scroll({
      behavior: "instant", // Prevent scrolling issues in combination with css scroll-snap-type
      left:
        this.#scrollContainer.scrollLeft - this.#scrollContainer.clientWidth,
    });
  };

  #updateButtonsVisibility() {
    if (this.isRestricted()) {
      const totalItemsCount = parseInt(this.totalItems, 10);
      const currentItemNumber = Number(this.#currentItem.textContent);

      if (totalItemsCount <= 1) {
        this.#nextButton.inert = true;
        this.#prevButton.inert = true;
      } else if (totalItemsCount >= 1) {
        this.#nextButton.inert = false;

        this.#prevButton.inert = currentItemNumber === 1;

        if (currentItemNumber === totalItemsCount) {
          this.#nextButton.inert = true;
        }
      }
    } else {
      this.#nextButton!.inert =
        Math.ceil(
          this.#scrollContainer.scrollLeft + this.#scrollContainer.clientWidth,
        ) >= this.#scrollContainer.scrollWidth; // Prevent rounding issues in zoomed browsers
      this.#prevButton!.inert = this.#scrollContainer.scrollLeft === 0;
    }
  }

  #updateItemCounter(itemNumber?: string) {
    if (itemNumber) {
      this.#currentItem.textContent = itemNumber;
      this.#itemCounter.hidden = false;
    } else {
      this.#itemCounter.hidden = true;
    }
  }

  #updateNavigationPosition(initial = true) {
    const imageHeight =
      this.#items[0]
        ?.querySelector(".group-gallery-item__figure")
        ?.getBoundingClientRect().height || 0;

    const itemCounter = this.#itemCounter;

    if (document.body.offsetWidth <= 767) {
      this.#navigation!.style.insetBlockStart = `${imageHeight + 24}px`; // 24px for the padding below the image
      this.#itemCounter.style.insetBlockStart = `${imageHeight + 24 + 9}px`; // 24px for the padding below the image, 9px to center it vertically in the middle of the nav buttons

      // Center the counter horizontally on the page
      this.#itemCounter.style.insetInlineEnd = "50%";
      this.#itemCounter.style.transform = "translateX(50%)";
    } else {
      this.#navigation!.style.insetBlockStart = `${imageHeight + 40}px`; // 40px for the padding below the image
      this.#itemCounter.style.insetBlockStart = `${imageHeight + 40 + 48 + 8}px`; // 40px for the padding below the image, 48px for the navigation, 8 px for padding between itemcounter and navigation
      this.#itemCounter.style.insetInlineEnd = `${this.#navigation!.offsetWidth - ((initial ? 7 : 0) + itemCounter.offsetWidth) / 2 + 24}px`; // To center the itemcounter in the middle, we need to substract the itemcounter width from the navigation width, divide it by two and add the remaining padding (currently 24). The itemcounter width here is initially the offsetwidth + 7, because the initial 1 is not loaded in time; during resizing this is not necessary anymore
    }
  }

  #updateBrowserUrl(itemUrl?: string) {
    if (!itemUrl) return;
    const currentUrl = new URL(window.location.href);
    const updatedUrl = new URL(itemUrl);
    currentUrl.searchParams.forEach((value, key) => {
      updatedUrl.searchParams.set(key, value);
    });
    try {
      window.history.replaceState(null, "", updatedUrl.toString());
    } catch (error) {
      console.error("An unexpected error occurred:", error);
    }
  }

  disconnectedCallback() {
    if (this.#nextButton && !this.isRestricted()) {
      this.#nextButton.removeEventListener("click", this.#scrollNext);
    }
    if (this.#prevButton && !this.isRestricted()) {
      this.#prevButton.removeEventListener("click", this.#scrollPrevious);
    }
    this.#itemsObserver.disconnect();
    this.#interactionObserver?.disconnect();
    this.#mutationObserver.disconnect();

    if (this.isRestricted()) {
      this.#scrollContainer.removeEventListener("wheel", this.#handleWheel);
      this.#scrollContainer.removeEventListener(
        "touchstart",
        this.#handleTouchStart,
      );
      this.#scrollContainer.removeEventListener(
        "touchmove",
        this.#handleTouchMove,
      );
    }

    if (this.#textPosition === "below") {
      window.removeEventListener("resize", this.#boundUpdateNavigationPosition);
    }
  }
}

"customElements" in window &&
  customElements.get("ws-gallery") === undefined &&
  customElements.define("ws-gallery", Gallery);

declare global {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface HTMLElementTagNameMap {
    "ws-gallery": Gallery;
  }
}
